'use client';

import { useState } from 'react';
import { flushSync } from 'react-dom';
import { TextField, Divider, Checkbox, Loader } from 'ui';
import { useTranslation } from '@/i18n/client';
import { useProductList } from '@/modules/product/context/productList';
import { useCurrentLocale } from '@/shared/utils/client';
import { fetchBrandsFilterList } from '@/modules/brands/api';
import { fetchFilterList } from '@/modules/product/api/productList';

import { formatFiltersBeforeSend } from '../filterBar/utils';
import { DiscountFilterProps } from './types';

export const DiscountFilter = ({
	initialValues,
	filteredValues,
	selectedValues,
	sidebarFilters,
	slug,
	unitName,
	setSidebarFilters,
	setSidebarFilteredFilterList,
}: DiscountFilterProps) => {
	const { t } = useTranslation(['common']);
	const locale = useCurrentLocale();
	const { categorySlug, brandSlug } = useProductList();
	const [search, setSearch] = useState('');
	const [values, setValues] = useState(initialValues);
	const [isLoading, setIsLoading] = useState(false);

	const handleSearchChange = (value: string) => {
		setValues(
			initialValues.filter((v) =>
				v.value.toLocaleLowerCase(locale).trim().includes(value.toLocaleLowerCase(locale).trim())
			)
		);
		setSearch(value);
	};

	const handleSelectValue = async (selected: boolean, name: string) => {
		const newSidebarFiltersValue = [...selectedValues] as string[];

		if (!selected) {
			if (newSidebarFiltersValue.indexOf(name) > -1) {
				return;
			}

			newSidebarFiltersValue.push(name);
		} else {
			const valueIndex = newSidebarFiltersValue.indexOf(name);

			if (newSidebarFiltersValue.indexOf(name) < 0) {
				return;
			}

			newSidebarFiltersValue.splice(valueIndex, 1);
		}

		flushSync(() => {
			setSidebarFilters((oldValue) => {
				const newValue = [...oldValue];

				const filterIndex = newValue.findIndex((filter) => filter.slug === slug);

				newValue[filterIndex].selectedValue = newSidebarFiltersValue;

				return newValue;
			});
		});
		setIsLoading(true);

		const newFilters = formatFiltersBeforeSend([...sidebarFilters]);

		if (newSidebarFiltersValue.length > 0) {
			newFilters[slug] =
				unitName === null
					? newSidebarFiltersValue
					: newSidebarFiltersValue.map((value) => value.replace(` ${unitName}`, ''));
		} else {
			delete newFilters[slug];
		}

		const response = brandSlug
			? await fetchBrandsFilterList(brandSlug, newFilters)
			: await fetchFilterList(categorySlug, newFilters);

		setSidebarFilteredFilterList(response);
		setIsLoading(false);
	};

	return (
		<>
			{initialValues.length >= 10 && (
				<>
					<TextField
						label={t('common.search')}
						value={search}
						onChange={(e) => handleSearchChange(e.target.value)}
					/>
					<Divider disableBackground bottom={15} />
				</>
			)}
			<ul>
				{values
					.sort((a, b) => (a.value > b.value ? 1 : b.value > a.value ? -1 : 0))
					.map((value, index) => {
						const disabled = !filteredValues.find((filter) => filter.key === value.key);

						if (disabled) {
							return;
						}

						return (
							<li key={value.key}>
								<Checkbox
									key={index}
									onChange={() => handleSelectValue(selectedValues.includes(value.key), value.key)}
									checked={selectedValues.includes(value.key)}
									disabled={disabled}
									position="start"
								>
									{value.value}
								</Checkbox>
							</li>
						);
					})}
				{values
					.sort((a, b) => (a.value > b.value ? 1 : b.value > a.value ? -1 : 0))
					.map((value, index) => {
						const disabled = !filteredValues.find((filter) => filter.key === value.key);

						if (!disabled) {
							return;
						}

						return (
							<li key={value.key}>
								<Checkbox
									key={index}
									onChange={() => handleSelectValue(selectedValues.includes(value.key), value.key)}
									checked={selectedValues.includes(value.key)}
									disabled={disabled}
									position="start"
								>
									{value.value}
								</Checkbox>
							</li>
						);
					})}
			</ul>
			{isLoading && <Loader />}
		</>
	);
};
